import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { TIMEZONES } from '../../lib/time.js';
import { ApiError } from '../../api/index.js';

const EMPTY = { login: '', displayName: '', role: 'user', timezone: 'UTC', password: '', isActive: true };

export function UserFormModal({ open, mode, initial, onClose, onSave, currentUserId }) {
  const isCreate = mode === 'create';
  const [values, setValues] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    if (isCreate) {
      setValues({ ...EMPTY });
    } else if (initial) {
      setValues({
        login: initial.login || '',
        displayName: initial.displayName || '',
        role: initial.role || 'user',
        timezone: initial.timezone || 'UTC',
        password: '',
        isActive: initial.isActive ?? true,
      });
    }
  }, [open, isCreate, initial]);

  const isSelf = !isCreate && initial?.id === currentUserId;

  const validation = useMemo(() => {
    const errors = {};
    if (!/^[A-Za-z0-9][A-Za-z0-9._-]{1,62}[A-Za-z0-9]$/.test(values.login.trim())) {
      errors.login = 'Login must be 3-64 chars, letters/digits/.-_, start and end alphanumeric.';
    }
    if (isCreate && (values.password.length < 12 || values.password.length > 128)) {
      errors.password = 'Password must be 12-128 characters.';
    }
    if (values.displayName && values.displayName.length > 128) {
      errors.displayName = 'Display name must be at most 128 characters.';
    }
    return errors;
  }, [values, isCreate]);

  const canSubmit =
    Object.keys(validation).length === 0 && !busy && values.login.trim().length > 0;

  function update(field, v) {
    setValues((prev) => ({ ...prev, [field]: v }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        login: values.login.trim(),
        displayName: values.displayName.trim() || null,
        role: values.role,
        timezone: values.timezone,
      };
      if (!isCreate) payload.isActive = values.isActive;
      if (isCreate) payload.password = values.password;
      await onSave(payload);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.payload?.error || err.message
          : err?.message || 'Operation failed.',
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={isCreate ? 'Create user' : `Edit ${initial?.login || 'user'}`}
      description={
        isCreate
          ? 'Register a new operator account.'
          : 'Update operator details. Use the password reset action to set a new password.'
      }
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={busy} data-skip-autofocus>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary"
          >
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" />
                Saving…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isCreate ? 'fa-plus' : 'fa-floppy-disk'}`} aria-hidden="true" />
                {isCreate ? 'Create user' : 'Save changes'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="login" className="field-label">Login</label>
            <input
              id="login"
              className="field"
              value={values.login}
              onChange={(e) => update('login', e.target.value)}
              placeholder="operator.smith"
              autoComplete="off"
              spellCheck="false"
              disabled={busy}
            />
            {validation.login && <p className="mt-1 text-xs text-rose-600">{validation.login}</p>}
          </div>

          <div>
            <label htmlFor="displayName" className="field-label">Display name <span className="text-mist font-normal">(optional)</span></label>
            <input
              id="displayName"
              className="field"
              value={values.displayName}
              onChange={(e) => update('displayName', e.target.value)}
              placeholder="John Smith"
              autoComplete="off"
              disabled={busy}
            />
            {validation.displayName && <p className="mt-1 text-xs text-rose-600">{validation.displayName}</p>}
          </div>
        </div>

        {isCreate && (
          <div>
            <label htmlFor="password" className="field-label">Initial password</label>
            <input
              id="password"
              type="password"
              className="field"
              value={values.password}
              onChange={(e) => update('password', e.target.value)}
              placeholder="Min. 12 characters"
              autoComplete="new-password"
              disabled={busy}
            />
            {validation.password && <p className="mt-1 text-xs text-rose-600">{validation.password}</p>}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="role" className="field-label">Role</label>
            <select
              id="role"
              className="field"
              value={values.role}
              onChange={(e) => update('role', e.target.value)}
              disabled={busy || isSelf}
            >
              <option value="user">User — read-only access</option>
              <option value="admin">Administrator — full control</option>
            </select>
            {isSelf && <p className="mt-1 text-xs text-mist">You cannot change your own role.</p>}
          </div>

          <div>
            <label htmlFor="timezone" className="field-label">Timezone</label>
            <select
              id="timezone"
              className="field"
              value={values.timezone}
              onChange={(e) => update('timezone', e.target.value)}
              disabled={busy}
            >
              {TIMEZONES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!isCreate && (
          <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-line accent-accent"
              checked={values.isActive}
              onChange={(e) => update('isActive', e.target.checked)}
              disabled={busy || isSelf}
            />
            <div>
              <div className="text-sm font-medium text-ink">Active</div>
              <div className="text-xs text-mist">
                Inactive users cannot sign in. {isSelf && 'You cannot deactivate your own account.'}
              </div>
            </div>
          </label>
        )}
      </form>
    </Modal>
  );
}
